// The car catalog and its moderation settings (/api/admin/cars/items, /settings).
import { useCallback, useEffect, useState } from 'react';
import {
  deleteCarItem, getCarItems, getCarSettings, replaceCarImage, updateCarItem, updateCarSettings,
  type AdminCar, type CarSettings,
} from '../api/client';
import { AuthedImage, CarForm, FilterPills, Modal, button, formFrom, formToBody, statusBadge, type CarFormValues } from './CarsShared';

type StatusFilter = 'all' | 'pending' | 'ai_approved' | 'approved' | 'rejected';

function SettingsCard() {
  const [settings, setSettings] = useState<CarSettings | null>(null);
  const [limit, setLimit] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    getCarSettings()
      .then(r => { setSettings(r.data.settings); setLimit(String(r.data.settings.submissionsPerUserPerDay)); })
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load settings'));
  }, []);

  const save = async (patch: Partial<CarSettings>) => {
    try {
      const r = await updateCarSettings(patch);
      setSettings(r.data.settings);
      setLimit(String(r.data.settings.submissionsPerUserPerDay));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save');
    }
  };

  if (error) return <div className="bg-red-50 text-red-600 px-3 py-2 rounded text-sm mb-3">{error}</div>;
  if (!settings) return null;
  return (
    <div className="bg-white rounded shadow-sm p-4 mb-4 grid gap-3 sm:grid-cols-3 text-sm">
      <label className="flex items-start gap-2">
        <input type="checkbox" className="mt-1" checked={settings.aiApprovedRequiresReview} onChange={e => save({ aiApprovedRequiresReview: e.target.checked })} />
        <span><span className="font-medium">AI-approved cars need a person</span><br /><span className="text-gray-500 text-xs">On: only approved items are public. Off: AI-approved items are public too.</span></span>
      </label>
      <label className="flex items-start gap-2">
        <input type="checkbox" className="mt-1" checked={settings.submissionsEnabled} onChange={e => save({ submissionsEnabled: e.target.checked })} />
        <span><span className="font-medium">Accept submissions</span><br /><span className="text-gray-500 text-xs">Off pauses new photos (the app gets submissions_paused).</span></span>
      </label>
      <div>
        <span className="font-medium">Submissions per person per day</span>
        <div className="flex gap-2 mt-1">
          <input type="number" min={0} max={1000} className="w-24 px-2 py-1 border border-gray-300 rounded" value={limit} onChange={e => setLimit(e.target.value)} />
          <button className={button.plain} onClick={() => save({ submissionsPerUserPerDay: Number(limit) })}>Save</button>
        </div>
        <span className="text-gray-500 text-xs">0 = no limit</span>
      </div>
    </div>
  );
}

function CarPanel({ car, onClose, onSaved, onDeleted }: { car: AdminCar; onClose: () => void; onSaved: (e: AdminCar) => void; onDeleted: (id: string) => void }) {
  const [form, setForm] = useState<CarFormValues>(() => formFrom(car));
  const [verified, setVerified] = useState(car.isVerified);
  const [current, setCurrent] = useState(car);
  const [saving, setSaving] = useState(false);

  const save = async (moderationStatus?: string) => {
    if (!form.name.trim()) { alert('Name is required'); return; }
    setSaving(true);
    try {
      const r = await updateCarItem(car.id, { ...formToBody(form), isVerified: verified, ...(moderationStatus ? { moderationStatus } : {}) });
      onSaved(r.data.car);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const replace = async (file: File | undefined) => {
    if (!file) return;
    setSaving(true);
    try {
      const r = await replaceCarImage(car.id, file);
      setCurrent(r.data.car);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirm('Delete this car for good? It is also removed from everyone’s owned and wishlist lists. Rejecting hides it instead.')) return;
    try {
      await deleteCarItem(car.id);
      onDeleted(car.id);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  return (
    <Modal
      title={current.name}
      onClose={onClose}
      footer={<>
        <button disabled={saving} className={button.plain} onClick={() => save()}>Save</button>
        <button disabled={saving} className={button.approve} onClick={() => save('approved')}>Save &amp; Approve</button>
        <button disabled={saving} className={button.pending} onClick={() => save('pending')}>Set Pending</button>
        <button disabled={saving} className={button.reject} onClick={() => save('rejected')}>Reject</button>
        <button disabled={saving} className={`${button.plain} ml-auto text-red-600`} onClick={remove}>Delete…</button>
      </>}
    >
      <div className="flex gap-4 flex-wrap">
        <div>
          <AuthedImage src={current.imageUrl} alt={current.name} className="w-56 h-56 object-contain rounded border" />
          <label className="block mt-2 text-xs text-blue-600 cursor-pointer">
            Replace photo (e.g. cropped, no people)
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={e => replace(e.target.files?.[0])} />
          </label>
        </div>
        <div className="flex-1 min-w-[200px] text-sm space-y-1">
          <div><span className={`px-1.5 py-0.5 text-xs font-medium rounded ${statusBadge(current.moderationStatus)}`}>{current.moderationStatus}</span></div>
          <div className="text-gray-500">Added {new Date(current.createdAt).toLocaleString()}</div>
          {current.aiConfidence != null && <div className="text-gray-500">AI confidence {current.aiConfidence}%</div>}
          <div className="text-xs text-gray-400">ID {current.id}</div>
          <label className="flex items-center gap-2 mt-2"><input type="checkbox" checked={verified} onChange={e => setVerified(e.target.checked)} /> Verified</label>
        </div>
      </div>
      <CarForm value={form} onChange={setForm} />
    </Modal>
  );
}

export default function CarsCatalog() {
  const [items, setItems] = useState<AdminCar[]>([]);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<AdminCar | null>(null);
  const limit = 25;

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async (p: number) => {
    setLoading(true);
    setError('');
    try {
      const res = await getCarItems(p, limit, status === 'all' ? undefined : status, debounced || undefined);
      setItems(prev => (p === 1 ? res.data.cars : [...prev, ...res.data.cars]));
      setTotal(res.data.pagination.total);
      setPage(p);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cars');
    } finally {
      setLoading(false);
    }
  }, [status, debounced]);

  useEffect(() => { load(1); }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-3">
        <div className="flex items-baseline gap-3">
          <h1 className="text-xl font-bold text-slate-800">Car Catalog</h1>
          <span className="text-sm text-gray-500">{total.toLocaleString()} items</span>
        </div>
        <button onClick={() => load(1)} className="px-3 py-1.5 bg-blue-500 text-white text-sm font-medium rounded hover:bg-blue-600">Refresh</button>
      </div>
      <SettingsCard />
      <div className="flex gap-3 items-center mb-3 flex-wrap">
        <input className="flex-1 max-w-md px-3 py-1.5 text-sm border border-gray-300 rounded" placeholder="Search name, series, color, toy number, barcode…" value={search} onChange={e => setSearch(e.target.value)} />
        <FilterPills<StatusFilter>
          value={status}
          onChange={setStatus}
          options={[
            { value: 'all', label: 'All' },
            { value: 'pending', label: 'Pending' },
            { value: 'ai_approved', label: 'AI Approved' },
            { value: 'approved', label: 'Approved' },
            { value: 'rejected', label: 'Rejected' },
          ]}
        />
      </div>
      {error && <div className="bg-red-50 text-red-600 px-3 py-2 rounded text-sm mb-2">{error}</div>}
      {!loading && items.length === 0 ? (
        <div className="bg-white rounded shadow-sm p-8 text-center text-gray-500">No cars yet. They arrive through submissions.</div>
      ) : (
        <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
          {items.map(car => (
            <div key={car.id} className="bg-white rounded shadow-sm p-2 cursor-pointer hover:ring-2 hover:ring-blue-300" onClick={() => setSelected(car)}>
              <AuthedImage src={car.imageUrl} alt={car.name} className="w-full aspect-square object-cover rounded" />
              <div className="mt-1 text-sm font-medium text-slate-800 truncate">{car.name}</div>
              <div className="text-xs text-gray-500 truncate">{[car.year, car.color, car.toyNumber].filter(Boolean).join(' · ') || '—'}</div>
              <div className="flex items-center gap-1 mt-0.5">
                <span className={`px-1.5 py-0.5 text-[0.65rem] font-medium rounded ${statusBadge(car.moderationStatus)}`}>{car.moderationStatus}</span>
                {car.isVerified && <span className="text-[0.65rem] text-green-700">✓ verified</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      {items.length < total && (
        <div className="text-center mt-3">
          <button disabled={loading} onClick={() => load(page + 1)} className={button.plain}>{loading ? 'Loading…' : 'Load more'}</button>
        </div>
      )}
      {selected && (
        <CarPanel
          car={selected}
          onClose={() => setSelected(null)}
          onSaved={e => { setItems(prev => prev.map(x => (x.id === e.id ? e : x))); setSelected(null); }}
          onDeleted={id => { setItems(prev => prev.filter(x => x.id !== id)); setTotal(t => t - 1); setSelected(null); }}
        />
      )}
    </div>
  );
}
